import React from 'react';
import { Tag, Typography, theme } from 'antd';
import { IconBriefcase, IconMapPin } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { IServiceSearchResult } from '../../interfaces/heavyDutyMachinery/IService';
import { getServiceCategoryById } from '../../constants/serviceOptions';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { formatCurrency } from '../../helpers/currencyHelper';
import { TEAL, TEAL_SOFT_BG, CORAL, CORAL_SOFT_BG } from '../../theme/creamyTokens';

const { Title, Text } = Typography;

interface ServiceSearchCardProps {
  service: IServiceSearchResult;
  onClick?: () => void;
  /** Minimal title+price rendering for tight spaces -- same role as HeavyDutyMachineryCard's own compact prop. */
  compact?: boolean;
}

/**
 * Guest-facing map/result card for a Service -- same visual language as
 * HeavyDutyMachineryCard and OperatorSearchCard (photo-shaped block, rounded
 * 16px/12px, title then price) so switching category on the map never feels
 * like a different app. A Service has no photo of its own, so it gets the same
 * placeholder box the operator and accessory cards already use, with a
 * briefcase in it.
 *
 * The price line is the part that had to be got right. A quote-only service
 * has NO price, and showing "desde 0 €" or an empty space would both be worse
 * than saying so: it says "presupuesto a medida", which is the truth and is
 * also the thing that makes the guest tap it. See Service.RequestMode.
 */
const ServiceSearchCard: React.FC<ServiceSearchCardProps> = ({ service, onClick, compact }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();

  const isQuoteOnly = service.requestMode === 'Quote';
  const chargesVisitFee = !!service.providerChargesVisitFee && (service.providerVisitFeeAmount ?? 0) > 0;
  const money = (amount: number) => formatCurrency(amount, service.hostCurrencyCode, i18n.language);

  // Hourly leads when there is a choice: it is the smaller, comparable number,
  // and a whole-job price sitting next to a €/h one in the same list tells the
  // guest nothing true about which is dearer.
  const priceLabel = isQuoteOnly
    ? translate(t, TRANSLATIONS.SERVICE_CARD_QUOTE_ONLY_PRICE)
    : service.hourlyRate != null
      ? `${money(service.hourlyRate)} ${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRICE_UNIT_HOUR)}`
      : service.fixedJobPrice != null
        ? `${money(service.fixedJobPrice)} ${translate(t, TRANSLATIONS.SERVICE_CARD_FIXED_JOB_SUFFIX)}`
        : translate(t, TRANSLATIONS.SERVICE_CARD_QUOTE_ONLY_PRICE);

  // The category's own translated name rather than the server's Spanish
  // ServiceCategory.Name -- the seed list is mirrored on this side with a
  // translation key each (see SERVICE_CATEGORY_OPTIONS). Falls back to what
  // the server sent if an id ever arrives that this build does not know.
  const categoryOption = getServiceCategoryById(service.serviceCategoryId);
  const categoryLabel = categoryOption ? translate(t, categoryOption.labelKey) : service.serviceCategoryName;

  const photoBox = (radius: number, iconSize: number) => (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: radius,
        background: token.colorFillTertiary,
        color: token.colorTextQuaternary,
      }}
    >
      <IconBriefcase size={iconSize} />
    </div>
  );

  if (compact) {
    return (
      <div onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default', width: 200 }}>
        <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 10' }}>{photoBox(12, 24)}</div>
        <div style={{ paddingTop: 8 }}>
          <Text strong style={{ display: 'block', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {service.title}
          </Text>
          <Text style={{ fontSize: 13 }}>{priceLabel}</Text>
        </div>
      </div>
    );
  }

  return (
    <div onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 10' }}>{photoBox(16, 32)}</div>

      <div style={{ paddingTop: 10, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Title level={5} style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {service.title}
        </Title>

        <Text type="secondary" style={{ fontSize: 13 }}>
          {service.providerDisplayName}
        </Text>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4, alignItems: 'center' }}>
          <Tag style={{ margin: 0, borderRadius: 999 }}>{categoryLabel}</Tag>
          {/* Said on the card, not left for the detail page: "pide
              presupuesto" is a different transaction from "reserva y paga",
              and finding that out after tapping is the kind of surprise that
              makes somebody close the app. */}
          {isQuoteOnly && (
            <Tag style={{ margin: 0, borderRadius: 999, background: TEAL_SOFT_BG, color: TEAL, border: 'none' }}>
              {translate(t, TRANSLATIONS.SERVICE_CARD_QUOTE_BADGE)}
            </Tag>
          )}
          {/* And if asking costs money, that goes on the card too, in coral
              rather than teal: it is the one thing here the guest is being
              asked to accept rather than offered. Finding it out on the
              professional's own screen at the end is an ambush. */}
          {chargesVisitFee && (
            <Tag style={{ margin: 0, borderRadius: 999, background: CORAL_SOFT_BG, color: CORAL, border: 'none' }}>
              {translate(t, TRANSLATIONS.VISIT_FEE_CARD_TAG, { amount: money(service.providerVisitFeeAmount!) })}
            </Tag>
          )}
          {service.city && (
            <Text type="secondary" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <IconMapPin size={12} />
              {service.city}
            </Text>
          )}
        </div>

        <div style={{ marginTop: 'auto', paddingTop: 8 }}>
          <Text strong style={{ fontSize: 15 }}>
            {priceLabel}
          </Text>
        </div>
      </div>
    </div>
  );
};

export default ServiceSearchCard;
